<script setup lang="ts">
import { Loader2, Sparkles, X, AlertTriangle, CircleAlert, CircleCheck, ExternalLink, Eye, FileText, Download, FileWarning, FolderInput, NotebookPen, Target } from 'lucide-vue-next'
import type { WorkflowRequest, WorkflowRequestField } from '~/types/web-chat'
import { useWorkflowRunsStore } from '~/stores/workflowRuns'
import { useWebChatStore } from '~/stores/web-chat'
import { isDraft } from '~~/shared/workflows/completeness'
import { runFilingDestination } from '~~/shared/web-chat/scope'
import { useRouteScopeOffer } from '~/composables/web-chat/useRouteScopeOffer'
import { storeToRefs } from 'pinia'
import { isBlankValue } from '~/utils/workflowFields'
import { requestFieldComplete } from '~/utils/workflowRunItems'
import { computePartyBlocks } from '~/utils/workflowPartyBlocks'

/**
 * A proposed workflow run, and then the run itself (MAR-437).
 *
 * ── SMALL ON PURPOSE ─────────────────────────────────────────────────────────────────────────────
 *
 * This is a chat card, not the run form. It shows what the agent understood, the handful of fields
 * still needed, and a way out to the full form. A template here carries 6 to 57 inputs, so rendering
 * all of them inline would bury the conversation — and the full form already exists, already handles
 * document pickers and batch tables, and is one click away with the values carried across.
 *
 * ── WHY IT SHOWS THE REAL FIELDS AT ALL ──────────────────────────────────────────────────────────
 *
 * MAR-38: a user was asked to approve a run with nothing on screen that could contradict what they
 * had asked for, and approved a contract rewriter for a request to draft a minuta. Every value going
 * into the run is visible and editable here, so a wrong proposal is recognisable as one.
 *
 * ── THE STATUS PHASE READS THE ACTIVITY CENTER, NOT A POLLER ─────────────────────────────────────
 *
 * Once `runId` is set the card shows position from `useWorkflowRunsStore().activeRuns`, which the
 * shared feed already keeps current (MAR-109). A private `setInterval` here is what previously
 * reported every run over ~2 minutes as a failure while it was still executing.
 */
const props = defineProps<{ request: WorkflowRequest }>()

const emit = defineEmits<{
  'accept-workflow': []
  'decline-workflow': []
  'open-workflow-form': []
}>()

/** How many fields render inline before the card sends the user to the full form. */
const INLINE_LIMIT = 4

/**
 * Types whose value needs a picker the chat card has no room for — and, since MAR-437, the line
 * between a BLANK the run can leave in the document and a WALL no completeness setting gets past.
 *
 * A missing paragraph of text becomes a placeholder somebody finishes in Word. A missing
 * `document_select` is not a blank at all: there is no document to work on, so offering to run it
 * anyway would be the card promising something it cannot deliver. The full form is both the honest
 * answer and the only one.
 */
const NEEDS_FULL_FORM = new Set(['document_select', 'party_select', 'multi_select'])

const { t } = useI18n()
/**
 * Renders the run's own failure sentence in the READER's language (#61's mechanism, reused here
 * rather than a second one being invented): the engine persists `error.messageI18n` alongside the
 * unchanged `error.message`, and older rows carry only the sentence, which is what it falls back to.
 */
const { renderMessage } = useI18nMessage()

const webChat = useWebChatStore()
const runs = useWorkflowRunsStore()
const { activeRuns } = storeToRefs(runs)

const run = computed(() => props.request.runId
  ? activeRuns.value.find(r => r.id === props.request.runId)
  : undefined)

/**
 * The single output the completed card's compact action row acts on — `undefined` when the run
 * produced none or several, matching `useWorkflowActions.openProducedDocument`'s own guard against
 * picking one of several as a guess. `undefined` here is what switches the card to the multi-output
 * list instead.
 */
const previewableOutput = computed(() => {
  const withDocs = (props.request.outputs ?? []).filter(o => o.documentId)
  return withDocs.length === 1 ? withDocs[0] : undefined
})

/**
 * The "Descargar" button on a finished card.
 *
 * Reuses `useConfirmCards.downloadDocument` (via the store) rather than re-fetching the file
 * here — the same function `useWorkflowActions.acceptWorkflow` already calls for the "Descargar
 * borrador" flow, so there is exactly one place that knows how to save a document's bytes.
 *
 * Tracks which output is in flight so the button can show its own spinner and disable itself,
 * rather than the card claiming — as the copy used to — that the file "se descargó" just because
 * a click happened.
 */
const downloadingId = ref<string | null>(null)
const downloadFailedId = ref<string | null>(null)
async function downloadOutput(out: { documentId?: string | null, name: string }) {
  // Guard by THIS output's id, not "any download in flight" — otherwise clicking a second
  // output's button while the first is still downloading silently no-ops with no visual cue,
  // since only the in-flight button itself renders as disabled/spinning.
  if (!out.documentId || downloadingId.value === out.documentId) return
  downloadingId.value = out.documentId
  downloadFailedId.value = null
  try {
    const ok = await webChat.downloadDocument({ id: out.documentId, name: out.name })
    if (!ok) downloadFailedId.value = out.documentId
  } finally {
    downloadingId.value = null
  }
}

/**
 * What the card shows once a run exists — read from the Activity Centre row, not kept in step with
 * it.
 *
 * The card used to render `request.status`, which a settle callback wrote. That callback fires when
 * a run leaves the feed, and a FAILED run does not leave: the endpoint keeps returning it as an
 * attention row. So a failed run left the card saying "Ejecutando…" indefinitely while the bell
 * showed it failed — two representations of one fact, disagreeing.
 *
 * The feed is the source of truth. `request.status` still owns the PROPOSAL (pending / declined) and
 * the optimistic gap between the click and the run existing, which the feed cannot know about.
 */
type RunPhase = 'running' | 'completed' | 'failed'
const phase = computed<RunPhase>(() => {
  switch (run.value?.status) {
    case 'completed': return 'completed'
    case 'failed':
    case 'cancelled': return 'failed'
    case undefined: break
    default: return 'running'
  }
  // No row yet — the run has not been reported by the feed. Fall back to what this card knows.
  if (props.request.status === 'completed') return 'completed'
  if (props.request.status === 'failed') return 'failed'
  return 'running'
})

/** Was this run started as a draft? One predicate, shared with the server, read by the whole card. */
const ranAsDraft = computed(() => isDraft(props.request.completeness))

/**
 * The status icon's colors — the icon SHAPE swaps in the template (spinner / pencil / check / alert),
 * this just says which semantic tint it sits in. Draft gets the warning tint, not success: a draft is
 * a "still needs you" state, not a finished one, even though the run itself succeeded.
 */
const statusIconClasses = computed(() => {
  if (phase.value === 'running') return { bg: 'bg-primary-light', fg: 'text-primary' }
  if (phase.value === 'failed') return { bg: 'bg-destructive/10', fg: 'text-destructive' }
  return ranAsDraft.value
    ? { bg: 'bg-warning/10', fg: 'text-warning-ink' }
    : { bg: 'bg-success/10', fg: 'text-success' }
})

/**
 * Whichever layer actually knows why it failed; the run row wins, since it is the authority.
 *
 * `request.error` passes through unkeyed on purpose — it is DATA the server set for this proposal,
 * not card copy. Only the last-resort sentence, which this card owns, is a key.
 */
const failureReason = computed(() =>
  renderMessage(run.value?.error?.messageI18n, run.value?.error?.message)
  || props.request.error
  || t('chat.workflowCard.runFailed'))

/**
 * Required fields not yet answered — what actually stands between the user and a run.
 *
 * "Answered" is the RUN FORM's rule (`requestFieldComplete` → `fieldComplete`, MAR-948): a required
 * checkbox left unticked is missing, and a select with no options (#862) is not, because no control
 * can ever fill it. So the card never promises a run the form would refuse, nor asks for a value
 * nothing can supply.
 */
const missing = computed(() => props.request.fields.filter(f => f.required && !requestFieldComplete(f)))

/** Missing inputs the card cannot ask for at all — a run is impossible until the form supplies them. */
const blockingMissing = computed(() => missing.value.filter(f => NEEDS_FULL_FORM.has(f.type)))

/**
 * Missing inputs that would simply come back as placeholders — the "N blanks" of a draft.
 *
 * This is what the card promises the user before they commit. Presenting a document with holes in it
 * as a finished one is the failure MAR-39 exists about; saying up front how many holes there will be
 * is how the output stops being a surprise.
 */
const blanks = computed(() => missing.value.filter(f => !NEEDS_FULL_FORM.has(f.type)))

/** Running now would produce a draft — both buttons say so, and both send `completeness: 'draft'`. */
const willBeDraft = computed(() => blanks.value.length > 0)

/**
 * WHERE THE OUTPUT WILL BE FILED — stated before the click (AlexAmin/marcus#67).
 *
 * The rule itself lives in `shared/web-chat/scope.ts` (`runFilingDestination`, which carries the
 * reasoning); this reads it off the LIVE `activeScope` — the very value `acceptWorkflow` sends as
 * `caseId` (`useWorkflowActions.ts` → `useWebChatStore().activeCaseId`). So the line reports what
 * THIS click will do rather than restating a rule: change the rung and the sentence changes with it.
 */
const filingDestination = computed(() => runFilingDestination(webChat.activeScope))

/**
 * The nudge toward case-scoping BEFORE this run generates (AlexAmin/marcus#246).
 *
 * Opening a case page does NOT scope chat to it — that coupling was deliberately torn out
 * (MAR-130/MAR-136: "El ámbito nunca es un efecto secundario de la navegación"). So a workflow
 * launched from the default composer while standing on a case page ran with `activeCaseId: null`,
 * and `linkRunOutputToCase` correctly no-op'd — the document never reached the case's Documents
 * tab, and nothing on this card said why.
 *
 * The fix is a NUDGE, not a silent default: `useRouteScopeOffer` already computes the case the
 * ROUTE is showing, and already refuses to offer it once a scope is set or the offer was
 * dismissed this sitting (rule 1 in that file) — this reuses that exact computation rather than
 * re-deriving "is the user on a case page" here. Restricted to `kind === 'case'`: a client-scoped
 * chat does not link to a case either (`runFilingDestination`), but there is no single case for
 * `acceptRouteOffer` to scope to, so a client offer would promise something accepting it can't do.
 */
const { routeOffer, acceptRouteOffer } = useRouteScopeOffer()
const caseScopeNudge = computed(() => {
  const offer = routeOffer.value
  return offer && offer.kind === 'case' ? offer : null
})

/**
 * What the agent filled, for the "here is what I understood" summary: a value it proposed, unless
 * that value does not answer a required field — an unticked required checkbox is listed as missing,
 * not also as understood.
 */
const filled = computed(() => props.request.fields.filter(f =>
  f.prefilled && !isBlankValue(f.value) && !missing.value.includes(f)))

/** Any field needing a picker means the full form is the only honest path. */
const needsFullForm = computed(() =>
  missing.value.some(f => NEEDS_FULL_FORM.has(f.type)) || missing.value.length > INLINE_LIMIT)

/** The few missing ones we will actually render inline. */
const inlineFields = computed(() =>
  needsFullForm.value ? [] : missing.value.slice(0, INLINE_LIMIT))

/**
 * Every field the card renders as a CONTROL while the proposal is pending (MAR-993, WF-1.11 — decided
 * by Alex): what the agent filled, plus the missing ones shown inline.
 *
 * What the agent understood used to be read-only text, so a wrong value — "hoy" filled as a date
 * months away — could only be fixed by leaving for the full form or asking the model again. The card
 * is where the user reads the proposal, so it is where they correct it.
 *
 * STICKY, IN THE REQUEST'S OWN FIELD ORDER. `filled` and `missing` are recomputed from the values, so
 * a field moves between them as it is typed into: the first character of a missing field made it
 * "filled", an emptied filled field became "missing", and either way its control unmounted under the
 * cursor. A key that has once been shown here therefore stays, and the list keeps the request's
 * order rather than the two lists' — the control a user is typing in never moves.
 *
 * A type that needs a picker (`NEEDS_FULL_FORM`) stays read-only text; its correction is the full
 * form, which is one click away.
 */
const shownAsControl = reactive(new Set<string>())
watchEffect(() => {
  for (const f of filled.value) if (!NEEDS_FULL_FORM.has(f.type)) shownAsControl.add(f.key)
  for (const f of inlineFields.value) shownAsControl.add(f.key)
})
const editableFields = computed(() => props.request.status === 'pending'
  ? props.request.fields.filter(f => shownAsControl.has(f.key))
  : [])

/** What the agent filled that is NOT a control: a picker-type value, or anything once no longer pending. */
const readOnlyFilled = computed(() => filled.value.filter(f => !editableFields.value.includes(f)))

/** The missing count only matters when the missing fields are NOT on the card as controls. */
const missingOffCard = computed(() => missing.value.filter(f => !editableFields.value.includes(f)))

/**
 * `WorkflowFieldControl` owns `inputValues[input.key]` — it reads and writes a shared bag. A proxy
 * onto `request.fields` keeps ONE copy of each value, which IS what the run submits (MAR-437
 * follow-up) — `field.value` is canonical now, not a local copy of something else, so a write here
 * is visible everywhere else that reads the same `request.fields` array (including the context
 * rail, which finds this exact request via `webChat.activeWorkflowRequest`) with no separate mirror
 * to keep in step.
 *
 * Every write also persists (debounced) via `webChat.syncWorkflowField` — the ONE place both
 * `WorkflowFieldControl`'s inline edits and `WorkflowEntityPartyFill`'s block fill go through, so
 * the document a user is previewing never lags behind what the card shows.
 */
const values = new Proxy({} as Record<string, any>, {
  get: (_t, key: string) => {
    const field = props.request.fields.find(f => f.key === key)
    return field?.value
  },
  set: (_t, key: string, value) => {
    webChat.syncWorkflowField(props.request, String(key), value)
    return true
  },
})

const blockingNotices = computed(() =>
  (props.request.notices ?? []).filter(n => n.severity === 'blocking'))

/**
 * Ejecutar is gated on the WALLS, not on completeness.
 *
 * It used to be `missing.length === 0`, which disabled the button on any blank field and left
 * "Descargar borrador" as the only way to run a half-filled proposal — and the checklist's actual
 * bug was the other half of that: when it WAS enabled it sent a strict run. Blanks now change what
 * is sent (`completeness: 'draft'`) and what the button says, not whether it can be pressed.
 */
const canRun = computed(() =>
  !props.request.loading && !!props.request.workflowId && blockingMissing.value.length === 0)

/**
 * Shape `WorkflowFieldControl` expects.
 *
 * The whole `constraints` object is passed through, with `options` merged over it for the callers
 * that only ever set that. Rebuilding it as `{ options }` — which is what this did — is why the card
 * could not autofill anything: `entitySource` is declared in `constraints`, and it was being dropped
 * one layer before the control that reads it, so the card asked the model to guess a DUI the client
 * record already held.
 */
function asInput(field: WorkflowRequestField) {
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    required: field.required,
    constraints: { ...(field.constraints ?? {}), options: field.options ?? field.constraints?.options },
  }
}

/**
 * Party blocks on the CARD (MAR-176's fill bar, brought to chat).
 *
 * Inputs sharing `constraints.entitySource.role` describe one party — the trabajador's name, DUI,
 * nacionalidad, domicilio — and the active client or case already holds those facts. One click fills
 * the whole block from stored data instead of the model inventing an identifier, which is the one
 * thing in a legal document that must not be silently wrong.
 *
 * Built from EVERY field, not just the four rendered inline: filling a field the card does not show
 * still removes it from `missing`, and can turn a proposal that needed the full form into one that
 * can run here. Blocks with nothing left to fill are dropped, so a fully-prefilled proposal shows no
 * bar at all.
 */
const partyBlocks = computed(() =>
  computePartyBlocks(
    props.request.fields,
    Object.fromEntries(props.request.fields.map(f => [f.key, f.value])),
  ).filter(block => block.anyBlank))
</script>

<template>
  <!-- No click on the card itself (MAR-437 follow-up, "compact row" design): every action here is
       its own explicit button, deliberately — a whole-card click that opens something was tried and
       reported as undiscoverable. -->
  <div class="mt-3 rounded-lg border border-primary/20 bg-primary-lighter p-3">
    <!-- One row shape for running/completed/failed alike — icon, title, badge/status, actions, all
         SIBLINGS in one flex container (never a separate icon column beside a text block: that is
         what made the icon and the title's vertical centers merely APPROXIMATE each other before).
         The icon's shape and tint carry the state (spinner / pencil / check / alert) instead of a
         status line — the proposal phase below is the one shape that still needs its own multi-line
         column, since it can carry a warning banner, filled-field summary and inline inputs. -->
    <template v-if="request.runId || request.status === 'running'">
      <div class="flex items-center gap-2">
        <div class="h-5 w-5 rounded-full flex items-center justify-center shrink-0" :class="statusIconClasses.bg">
          <Loader2 v-if="phase === 'running'" class="h-3 w-3 animate-spin" :class="statusIconClasses.fg" />
          <NotebookPen v-else-if="phase === 'completed' && ranAsDraft" class="h-3 w-3" :class="statusIconClasses.fg" />
          <CircleCheck v-else-if="phase === 'completed'" class="h-3 w-3" :class="statusIconClasses.fg" />
          <CircleAlert v-else class="h-3 w-3" :class="statusIconClasses.fg" />
        </div>
        <span class="text-sm font-semibold text-foreground truncate">{{ request.workflowName }}</span>
        <Badge v-if="phase === 'completed' && ranAsDraft" variant="secondary" class="shrink-0 h-4 px-1.5 py-0 text-[10px] font-bold tracking-wide">
          {{ t('chat.workflowCard.draftBadge') }}
        </Badge>
        <!-- Position, never a percentage: `completed_steps` of `step_count` IS a position. Three
             honest states, not one — before the POST answers there is no run to report on, so
             "Ejecutando" would claim something that has not started yet. -->
        <span v-if="phase === 'running'" class="text-xs text-muted-foreground shrink-0">
          <!-- `runs.stepProgress` is the SAME sentence the runs list already renders ("Paso {done}
               de {total}"), so the card and the list cannot drift apart in either language. -->
          <template v-if="run && run.stepCount > 0">{{ t('runs.stepProgress', { done: run.completedSteps + 1, total: run.stepCount }) }}</template>
          <template v-else-if="!request.runId">{{ t('chat.workflowCard.starting') }}</template>
          <template v-else>{{ t('chat.workflowCard.running') }}</template>
        </span>
        <div class="flex-1"></div>
        <template v-if="phase === 'completed' && previewableOutput">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            class="shrink-0 text-muted-foreground hover:text-foreground"
            :title="t('chat.workflowCard.preview')"
            :aria-label="t('chat.workflowCard.previewOf', { name: previewableOutput.name })"
            @click.stop="webChat.openDocPreview(previewableOutput.documentId!, previewableOutput.name)"
          >
            <Eye class="h-3.5 w-3.5" />
          </Button>
          <Button
            v-if="request.runId"
            as-child
            variant="ghost"
            size="icon-sm"
            class="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <NuxtLink :to="`/app/runs/${request.runId}`" :title="t('chat.workflowCard.viewRun')" :aria-label="t('chat.workflowCard.viewRun')" @click.stop>
              <ExternalLink class="h-3.5 w-3.5" />
            </NuxtLink>
          </Button>
          <Button
            type="button"
            size="sm"
            class="shrink-0 gap-1.5"
            :disabled="downloadingId === previewableOutput.documentId"
            @click.stop="downloadOutput(previewableOutput)"
          >
            <Loader2 v-if="downloadingId === previewableOutput.documentId" class="h-3 w-3 animate-spin" />
            <Download v-else class="h-3 w-3" />
            {{ t('chat.workflowCard.download') }}
          </Button>
        </template>
        <!-- Running or failed (or a multi-output completion, handled below): the one action that
             always applies is looking at the run itself. -->
        <Button
          v-else-if="request.runId"
          as-child
          variant="ghost"
          size="icon-sm"
          class="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <NuxtLink :to="`/app/runs/${request.runId}`" :title="t('chat.workflowCard.viewRun')" :aria-label="t('chat.workflowCard.viewRun')" @click.stop>
            <ExternalLink class="h-3.5 w-3.5" />
          </NuxtLink>
        </Button>
      </div>

      <!-- The blank COUNT breaks onto its own line, the same way the failure reason does below it —
           neither fits crammed into the row's badge or beside its buttons without competing for the
           one line meant to just be the title. -->
      <p v-if="phase === 'completed' && ranAsDraft" class="mt-1 pl-7 text-[11px] text-muted-foreground">
        {{ request.blanks ? t('chat.workflowCard.blanksLeft', { n: request.blanks }, request.blanks) : '' }}
        {{ t('chat.workflowCard.finishInWord') }}
      </p>
      <p v-if="phase === 'failed'" class="mt-1 pl-7 text-[11px] text-muted-foreground">
        {{ failureReason }}
      </p>
      <span v-if="phase === 'completed' && previewableOutput && downloadFailedId === previewableOutput.documentId" class="mt-1 block pl-7 text-[11px] text-destructive">
        {{ t('chat.workflowCard.downloadFailed') }}
      </span>

      <!-- Multiple deliverables (batch runs) are the one case the row above can't act on
           unambiguously — which file would "Descargar" mean? So they keep their own list, each with
           its own download. This is the exception; the row above is the common case. -->
      <ul v-if="phase === 'completed' && !previewableOutput && request.outputs?.length" class="mt-1.5 pl-7 space-y-1">
        <li v-for="out in request.outputs" :key="out.id" class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <button
            v-if="out.documentId"
            type="button"
            class="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            @click.stop="webChat.openDocPreview(out.documentId, out.name)"
          >
            <FileText class="h-3 w-3" /> {{ out.name }}
          </button>
          <span v-else class="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <FileText class="h-3 w-3" /> {{ out.name }}
          </span>
          <button
            v-if="out.documentId"
            type="button"
            class="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent-lighter hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="downloadingId === out.documentId"
            @click.stop="downloadOutput(out)"
          >
            <Loader2 v-if="downloadingId === out.documentId" class="h-3 w-3 animate-spin" />
            <Download v-else class="h-3 w-3" />
            {{ ranAsDraft ? t('chat.workflowCard.downloadDraft') : t('chat.workflowCard.download') }}
          </button>
          <span v-if="downloadFailedId === out.documentId" class="text-[11px] text-destructive">
            {{ t('chat.workflowCard.downloadFailed') }}
          </span>
        </li>
      </ul>
    </template>

    <!-- ── proposal phase ── the one shape still needing its own icon column, since it can carry a
         warning banner, a filled-field summary and inline inputs below the title. -->
    <div v-else class="flex items-start gap-2">
      <div class="mt-0.5 h-5 w-5 rounded-full bg-primary-light flex items-center justify-center shrink-0">
        <Sparkles class="h-3 w-3 text-primary" />
      </div>
      <div class="min-w-0 flex-1">
          <p class="text-xs font-medium text-foreground">{{ t('chat.workflowCard.confirmTitle') }}</p>

          <p v-if="request.loading" class="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 class="h-3 w-3 animate-spin" /> {{ t('chat.workflowCard.loading') }}
          </p>
          <template v-else>
            <p class="mt-0.5 text-xs text-muted-foreground leading-relaxed font-semibold">
              {{ request.workflowName }}
            </p>
            <p v-if="request.reason" class="mt-1 text-xs text-muted-foreground leading-relaxed">
              {{ request.reason }}
            </p>

            <!-- Authored warnings, before the user commits. The employment contract's says it covers
                 `tiempo indefinido` only — exactly the thing that makes a near-twin the wrong pick. -->
            <div
              v-for="notice in blockingNotices"
              :key="notice.message"
              class="mt-2 flex gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5"
            >
              <AlertTriangle class="mt-0.5 h-3 w-3 shrink-0 text-destructive" />
              <p class="text-[11px] leading-relaxed text-foreground">{{ notice.message }}</p>
            </div>

            <!-- CASE-SCOPE NUDGE (AlexAmin/marcus#246). Only while the proposal is still pending —
                 once it has run, the destination is a fact, not a choice. This does NOT auto-scope
                 (MAR-130); accepting is the same explicit `setScope` write the top-of-chat banner
                 uses, and Ejecutar stays enabled either way — nudged, never blocked. -->
            <div
              v-if="request.status === 'pending' && caseScopeNudge"
              class="mt-2 flex items-start gap-1.5 rounded-md border border-accent-ink/25 bg-accent-lightest px-2 py-1.5"
            >
              <Target class="mt-0.5 h-3 w-3 shrink-0 text-accent-ink" />
              <div class="min-w-0 flex-1">
                <i18n-t
                  keypath="chat.workflowCard.caseScopeNudgeBody"
                  tag="p"
                  class="text-[11px] leading-relaxed text-foreground"
                  scope="global"
                >
                  <template #name><span class="font-medium">{{ caseScopeNudge.name }}</span></template>
                </i18n-t>
                <button
                  type="button"
                  class="mt-1 inline-flex items-center gap-1 rounded-md border border-accent-ink bg-accent-light px-2 py-0.5 text-[11px] font-medium text-accent-light-foreground transition-colors hover:bg-accent-lighter"
                  @click="acceptRouteOffer"
                >
                  <Target class="h-3 w-3 shrink-0" />
                  {{ t('chat.workflowCard.caseScopeNudgeAccept') }}
                </button>
              </div>
            </div>

            <!-- What the agent understood. Without this the card states a name and nothing that
                 could contradict the user's request (MAR-38). -->
            <dl
              v-if="readOnlyFilled.length"
              class="mt-2 rounded-md border border-primary/15 bg-background/60 divide-y divide-primary/10"
            >
              <div v-for="field in readOnlyFilled" :key="field.key" class="flex gap-2 px-2 py-1.5">
                <dt class="shrink-0 text-[11px] text-muted-foreground">{{ field.label }}</dt>
                <dd class="min-w-0 flex-1 text-right text-[11px] text-foreground break-words">
                  {{ Array.isArray(field.value) ? (field.value as unknown[]).join(', ') : field.value }}
                </dd>
              </div>
            </dl>

            <!-- Fill a whole party from the active client or case (MAR-176's bar, on the card).
                 Above the fields because it WRITES them, and it writes fields the card does not
                 render too — a block can take a proposal that needed the full form and make it
                 runnable here. Renders nothing under a global scope or with no matching party. -->
            <div v-if="partyBlocks.length" class="mt-2 space-y-2">
              <WorkflowEntityPartyFill
                v-for="block in partyBlocks"
                :key="block.role"
                :role="block.role"
                :targets="block.targets"
                :id-type="block.idType"
                :kind="block.kind"
                :input-values="values"
              />
            </div>

            <!-- Every value the card can correct, and the few still needed — all as controls while the
                 proposal is pending (MAR-993). Anything needing a picker goes to the full form.
                 Wrapped in the same `WorkflowFormField` the run form uses (AlexAmin/marcus#796) —
                 without it these controls rendered with no visible label and no accessible name at
                 all, unlike everything else on the card. -->
            <div v-if="editableFields.length" class="mt-2 space-y-2">
              <WorkflowFormField v-for="field in editableFields" :key="field.key" :label="field.label" label-size="xs">
                <WorkflowFieldControl :input="asInput(field)" :input-values="values" />
              </WorkflowFormField>
            </div>

            <p v-if="blockingMissing.length" class="mt-2 text-[11px] text-muted-foreground">
              {{ t('chat.workflowCard.missingBlocking', { n: blockingMissing.length }) }}
            </p>
            <p v-else-if="missingOffCard.length" class="mt-2 text-[11px] text-muted-foreground">
              {{ t('chat.workflowCard.missingInForm', { n: missingOffCard.length }) }}
            </p>

            <!-- WHAT WILL HAPPEN, before the click.
                 Running with blanks is allowed and always was; what was missing is the card saying
                 so. A user who presses Ejecutar on a half-filled proposal and receives a document
                 full of `[SALARIO_MENSUAL]` was told nothing beforehand — and the run used to be
                 sent as strict anyway, so it simply failed. Both buttons now send
                 `completeness: 'draft'`, and this line is the warning that makes that honest. -->
            <p
              v-if="request.status === 'pending' && willBeDraft && canRun"
              class="mt-2 flex gap-1.5 rounded-md border border-warning-ink/30 bg-warning/10 px-2 py-1.5 text-[11px] leading-relaxed text-foreground"
            >
              <FileWarning class="mt-0.5 h-3 w-3 shrink-0 text-warning-ink" />
              <!-- i18n-t so `borrador` keeps its emphasis inside the sentence instead of the sentence
                   being split into fragments a translator cannot reorder. -->
              <i18n-t keypath="chat.workflowCard.draftWarning" tag="span" :plural="blanks.length" scope="global">
                <template #draft><span class="font-medium">{{ t('chat.workflowCard.draftWord') }}</span></template>
                <template #n>{{ blanks.length }}</template>
              </i18n-t>
            </p>

            <!-- Where it lands, on the same footing as the draft warning above: what the user gets
                 back, said before they commit rather than discovered afterwards (#67). Muted, not a
                 warning tint — for every rung this is a statement of destination, and the library is
                 a correct home for a client-scoped run, not a degraded one. -->
            <p
              v-if="request.status === 'pending' && canRun"
              class="mt-2 flex gap-1.5 text-[11px] leading-relaxed text-muted-foreground"
            >
              <FolderInput class="mt-0.5 h-3 w-3 shrink-0" />
              <span>{{ renderMessage(filingDestination.message, filingDestination.text) }}</span>
            </p>

            <div v-if="request.status === 'pending'" class="mt-2.5 flex flex-wrap gap-1.5">
              <!-- One button, not two: "Descargar borrador" used to run the identical request and
                   open the identical preview panel, differing only in whether it ALSO triggered an
                   automatic download — a distinction the split-preview panel already makes moot,
                   since its own header has a Download button once the result is showing. Generating
                   and opening the preview is now simply the default, single action. -->
              <Button
                type="button"
                size="sm"
                class="gap-1.5"
                :disabled="!canRun"
                @click="emit('accept-workflow')"
              >
                <Sparkles class="h-3 w-3" />
                {{ willBeDraft ? t('chat.workflowCard.generateDraft') : t('chat.workflowCard.run') }}
              </Button>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent-lighter hover:text-foreground transition-colors"
                @click="emit('open-workflow-form')"
              >
                <ExternalLink class="h-3 w-3" />
                {{ t('chat.workflowCard.openFullForm') }}
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                @click="emit('decline-workflow')"
              >
                <X class="h-3 w-3" />
                {{ t('chat.workflowCard.cancel') }}
              </button>
            </div>

            <p v-else-if="request.status === 'failed'" class="mt-2.5 text-[11px] text-muted-foreground">
              {{ request.error || t('chat.workflowCard.prepareFailed') }}
            </p>
            <p v-else-if="request.status === 'declined'" class="mt-2.5 text-[11px] text-muted-foreground">
              {{ t('chat.workflowCard.declined') }}
            </p>
          </template>
      </div>
    </div>
  </div>
</template>
